import { useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { ModalSize, QUERIES, TipoReceitaSncr } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { useModal } from 'app/components'
import { SncrRetiradaModal } from '../components/SncrRetiradaModal'
import {
  ROTULO_TIPO_RECEITA_SNCR,
  SncrContexto,
  SncrRetiradaResultado,
  exigeNumeracaoSncr,
  saldoDoTipo,
} from '../core/_models'
import { getSncrContexto } from '../core/_requests'

export type NecessidadesSncr = Partial<Record<TipoReceitaSncr, number>>

const chaveContexto = (idEmpresa?: number) => [QUERIES.SNCR_CONTEXTO, idEmpresa]

/** Contexto SNCR (inscrição e saldos) do profissional na empresa, com cache curto compartilhado. */
export const useSncrContexto = (idEmpresa?: number) =>
  useQuery(chaveContexto(idEmpresa), () => getSncrContexto(idEmpresa as number), {
    enabled: !!idEmpresa,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })

export const useSncr = () => {
  const { t } = useT()
  const { showModal } = useModal()
  const queryClient = useQueryClient()

  /** Sempre fresco: decisão de bloquear/liberar a assinatura não pode usar cache. */
  const carregarContexto = useCallback(
    (idEmpresa: number) =>
      queryClient.fetchQuery<SncrContexto>(
        chaveContexto(idEmpresa),
        () => getSncrContexto(idEmpresa),
        {
          staleTime: 0,
        }
      ),
    [queryClient]
  )

  const invalidarContexto = useCallback(
    (idEmpresa: number) => queryClient.invalidateQueries(chaveContexto(idEmpresa)),
    [queryClient]
  )

  const showRetiradaModal = useCallback(
    (idEmpresa: number, tipo: TipoReceitaSncr, contexto: SncrContexto) =>
      new Promise<SncrRetiradaResultado | null>((resolve) => {
        showModal({
          title: t('sncr.modal.titulo', { tipo: ROTULO_TIPO_RECEITA_SNCR[tipo] }),
          size: ModalSize.Medium,
          content: (
            <SncrRetiradaModal
              idEmpresa={idEmpresa}
              tipo={tipo}
              contexto={contexto}
              onConfirm={(resultado) => {
                invalidarContexto(idEmpresa)
                resolve(resultado)
              }}
            />
          ),
          onClose: () => resolve(null),
        })
      }),
    [showModal, t, invalidarContexto]
  )

  /**
   * Garante saldo SNCR suficiente para cada tipo antes de finalizar receitas controladas. Só age
   * quando a numeração é exigida (`exigirNumeracao`): abre a retirada enquanto faltar número e
   * devolve `false` se o médico desistir. Sem exigência, libera direto — o backend finaliza sem
   * número e registra no atendimento; o médico não vê nenhum diálogo do SNCR. Notificação de receita
   * exige número sempre; só os blocos (RCE/RET) seguem a flag (`exigeNumeracaoSncr`).
   */
  const ensureSncr = useCallback(
    async (idEmpresa: number | undefined, necessidades: NecessidadesSncr): Promise<boolean> => {
      const comNecessidade = (Object.keys(necessidades) as TipoReceitaSncr[]).filter(
        (tipo) => (necessidades[tipo] ?? 0) > 0
      )
      if (!idEmpresa || comNecessidade.length === 0) return true

      let contexto = await carregarContexto(idEmpresa)
      if (!contexto.habilitado) return true
      const tipos = comNecessidade.filter((tipo) => exigeNumeracaoSncr(contexto, tipo))

      for (const tipo of tipos) {
        const necessario = necessidades[tipo] ?? 0
        while ((saldoDoTipo(contexto, tipo)?.saldo ?? 0) < necessario) {
          // Bloco já retirado e sem registro ainda: outra retirada daria 422, e o número só existe
          // depois que o backend concluir. O rascunho fica e o médico finaliza de novo em instantes.
          if (saldoDoTipo(contexto, tipo)?.retiradaEmAndamento) {
            throw new Error(t('sncr.erro.registro_pendente'))
          }
          const resultado = await showRetiradaModal(idEmpresa, tipo, contexto)
          if (!resultado) return false
          if (resultado.registroPendente) throw new Error(t('sncr.erro.registro_pendente'))
          contexto = await carregarContexto(idEmpresa)
        }
      }
      return true
    },
    [carregarContexto, showRetiradaModal, t]
  )

  return { ensureSncr, showRetiradaModal, carregarContexto, invalidarContexto }
}
